import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
    TASK_STATUS,
    TASK_TYPE,
    TASK_PRIORITY,
    TASK_PRIORITY_LABELS,
    CONTACT_REASON_LABELS,
    CONTACT_REASON_ICONS
} from '../../../../constants/tasks';
import './OperationalKanban.css';
import { getDaysDifferenceFromToday } from '../../../../utils/date';

const columnOrder = [
    TASK_STATUS.SCHEDULED,
    TASK_STATUS.NEW,
    TASK_STATUS.WAITING,
    TASK_STATUS.OVERDUE,
    TASK_STATUS.DONE
];

const columnMeta = {
    [TASK_STATUS.SCHEDULED]: { icon: 'mdi-calendar-clock', tone: 'slate', hint: 'Programado' },
    [TASK_STATUS.NEW]: { icon: 'mdi-inbox-outline', tone: 'blue', hint: 'Para Contatar' },
    [TASK_STATUS.WAITING]: { icon: 'mdi-clock-time-four-outline', tone: 'purple', hint: 'Aguardando Retorno' },
    [TASK_STATUS.OVERDUE]: { icon: 'mdi-alert-circle-outline', tone: 'red', hint: 'Atrasado' },
    [TASK_STATUS.DONE]: { icon: 'mdi-check-circle-outline', tone: 'green', hint: 'Concluído' },
};

const formatScheduleMeta = (rawDate) => {
    if (!rawDate) return null;
    const diffDays = getDaysDifferenceFromToday(rawDate);
    if (diffDays === null) return null;

    try {
        const datePart = rawDate.split('T')[0];
        const parts = datePart.split('-');
        if (parts.length !== 3) return null;
        const [, month, day] = parts;

        return {
            label: diffDays < 0 ? `${Math.abs(diffDays)}d atraso` : diffDays === 0 ? 'Hoje' : `D+${diffDays}`,
            date: `${day}/${month}`,
            overdue: diffDays < 0,
        };
    } catch {
        return null;
    }
};

const abbreviateName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length <= 2) return fullName;
    return `${parts[0]} ${parts[parts.length - 1]}`;
};

const ATTEMPT_CONFIG = {
    0: { label: '1ª tentativa', hint: 'Primeiro contato — apresente-se e ofereça ajuda.', color: '#3b82f6' },
    1: { label: '2ª tentativa', hint: 'Mude o canal se possível. Aguarde 3 dias.', color: '#f59e0b' },
    2: { label: '3ª tentativa', hint: 'Última antes de Atrasado. Tom mais curto.', color: '#f97316' },
    3: { label: '4ª tentativa', hint: 'Card em Atrasado. Tente antes do encerramento.', color: '#ef4444' },
    4: { label: 'Encerramento', hint: 'Envie mensagem de saída elegante e registre "Desistiu".', color: '#6b7280' },
};

const getAttemptConfig = (attempts) => ATTEMPT_CONFIG[Math.min(attempts ?? 0, 4)];

const OperationalKanban = ({ columns, onDragEnd, onItemClick, onNewTaskClick }) => {
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="operational-kanban-shell position-relative">

                {columnOrder.map((columnId) => {
                    const column = columns[columnId];
                    const isOverdue = columnId === TASK_STATUS.OVERDUE;
                    const meta = columnMeta[columnId] || columnMeta[TASK_STATUS.NEW];

                    return (
                        <div key={column.id} className={`operational-kanban-column column-${meta.tone}`}>
                            <div className="operational-column-header">
                                <div className="d-flex align-items-center gap-2 overflow-hidden">
                                    <div className="operational-column-icon">
                                        <i className={`mdi ${meta.icon}`}></i>
                                    </div>
                                    <div className="overflow-hidden">
                                        <h6 className="operational-column-title">{column.title}</h6>
                                        <span className="operational-column-hint">{meta.hint}</span>
                                    </div>
                                </div>
                                <span className={`operational-column-count ${isOverdue && column.items.length > 0 ? 'is-alert' : ''}`}>
                                    {column.items.length}
                                </span>
                            </div>

                            <Droppable droppableId={column.id}>
                                {(provided, snapshot) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className={`operational-column-scroll custom-scrollbar ${snapshot.isDraggingOver ? 'is-dragging-over' : ''}`}
                                    >
                                        {column.items.map((item, index) => {
                                            const scheduleMeta = formatScheduleMeta(item.rawDate);
                                            const displayBadge = item.contactReason
                                                ? (CONTACT_REASON_LABELS[item.contactReason] || item.badgeInfo?.text)
                                                : item.badgeInfo?.text;
                                            const reasonIcon = item.contactReason ? CONTACT_REASON_ICONS[item.contactReason] : null;
                                            const badgeColor = reasonIcon?.color || item.badgeInfo?.color || 'primary';
                                            const priorityLabel = TASK_PRIORITY_LABELS?.[item.priority] || item.priority;
                                            const assignee = item.assignedEmployees?.[0];
                                            const attempts = item.contactAttempts ?? 0;
                                            const attemptCfg = getAttemptConfig(attempts);

                                            return (
                                                <Draggable key={item.id} draggableId={item.id} index={index}>
                                                    {(providedDrag, snapshotDrag) => (
                                                        <div
                                                            ref={providedDrag.innerRef}
                                                            {...providedDrag.draggableProps}
                                                            {...providedDrag.dragHandleProps}
                                                            className="operational-card-wrap"
                                                            style={{ ...providedDrag.draggableProps.style }}
                                                            onClick={() => onItemClick(item)}
                                                        >
                                                            <article className={`operational-task-card ${snapshotDrag.isDragging ? 'is-dragging' : ''} ${item.status === 'overdue' ? 'is-overdue' : ''}`}>
                                                                {/* Faixa vertical removida para evitar poluição visual */}

                                                                {/* Cabeçalho do Card */}
                                                                <div className="operational-card-head d-flex align-items-center justify-content-between mb-2">
                                                                    <span className={`operational-reason-badge text-${badgeColor}`} title={displayBadge}>
                                                                        {reasonIcon?.icon && <i className={`mdi ${reasonIcon.icon} me-1`}></i>}
                                                                        {displayBadge}
                                                                    </span>
                                                                    {(scheduleMeta || item.date) && (
                                                                        <span className={`operational-date-badge ${scheduleMeta?.overdue ? 'is-overdue' : ''}`}>
                                                                            <i className="mdi mdi-calendar-blank-outline me-1"></i>
                                                                            {scheduleMeta ? scheduleMeta.date : ''}
                                                                            {scheduleMeta && item.date && ' • '}
                                                                            {item.date || ''}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {/* Título principal do Card */}
                                                                <h6 className="operational-card-title">{item.title}</h6>

                                                                {/* Badge de tentativa de contato */}
                                                                {item.type === TASK_TYPE.CONTACT && (
                                                                    <div
                                                                        title={attemptCfg.hint}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            background: attemptCfg.color + '18',
                                                                            border: `1px solid ${attemptCfg.color}40`,
                                                                            borderRadius: '6px',
                                                                            padding: '2px 7px',
                                                                            marginTop: '6px',
                                                                            marginLeft: '12px',
                                                                            fontSize: '10.5px',
                                                                            fontWeight: '700',
                                                                            color: attemptCfg.color,
                                                                            fontFamily: 'Outfit, sans-serif',
                                                                            cursor: 'help',
                                                                        }}
                                                                    >
                                                                        <i className="mdi mdi-phone-outline" style={{ fontSize: '11px' }}></i>
                                                                        {attemptCfg.label}
                                                                        <i className="mdi mdi-information-outline" style={{ fontSize: '11px', opacity: 0.6 }}></i>
                                                                    </div>
                                                                )}

                                                                {/* Rodapé - Avatar + Nome Abreviado do Cliente e Prioridade minimalista */}
                                                                <div className="operational-card-footer d-flex align-items-center justify-content-between mt-2">
                                                                    <div className="d-flex align-items-center overflow-hidden" style={{ maxWidth: '65%', gap: '8px' }}>
                                                                        {item.memberNumber && (
                                                                            <div className="operational-avatar" style={{ marginLeft: 0, flexShrink: 0 }} title={item.memberName}>
                                                                                {item.memberPhotoUrl ? (
                                                                                    <img src={item.memberPhotoUrl} alt="Cliente" />
                                                                                ) : (
                                                                                    <span>{(item.memberName?.charAt(0) || 'C').toUpperCase()}</span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {item.memberName && (
                                                                            <span 
                                                                                className="operational-client-name-clean text-truncate" 
                                                                                title={item.memberName}
                                                                                style={{
                                                                                    fontFamily: 'Outfit, sans-serif',
                                                                                    fontSize: '11.5px',
                                                                                    fontWeight: '700',
                                                                                    color: 'var(--color-brand-secondary)',
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                }}
                                                                            >
                                                                                {abbreviateName(item.memberName)}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <div className="operational-card-signals-clean" style={{ gap: '8px' }}>
                                                                        {assignee && (
                                                                            <div 
                                                                                className="operational-avatar is-employee" 
                                                                                style={{ 
                                                                                    width: '24px', 
                                                                                    height: '24px', 
                                                                                    flexShrink: 0, 
                                                                                    margin: 0,
                                                                                    boxShadow: 'none',
                                                                                    border: '1.5px solid #482712'
                                                                                }} 
                                                                                title={`Consultor Responsável: ${assignee.name}`}
                                                                            >
                                                                                {assignee.photoUrl ? (
                                                                                    <img src={assignee.photoUrl} alt={assignee.name} />
                                                                                ) : (
                                                                                    <span style={{ fontSize: '9px', fontWeight: '900', color: '#482712' }}>
                                                                                        {(assignee.name?.charAt(0) || 'R').toUpperCase()}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {item.priority && (
                                                                            <span className={`operational-priority`} title={`Prioridade: ${priorityLabel}`}>
                                                                                {item.priority === TASK_PRIORITY.HIGH && <span className="badge-priority high"><i className="mdi mdi-chevron-double-up me-0.5"></i>Alta</span>}
                                                                                {item.priority === TASK_PRIORITY.NORMAL && <span className="badge-priority normal"><i className="mdi mdi-equal me-0.5"></i>Média</span>}
                                                                                {item.priority === TASK_PRIORITY.LOW && <span className="badge-priority low"><i className="mdi mdi-chevron-double-down me-0.5"></i>Baixa</span>}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </article>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}

                                        {column.items.length === 0 && (
                                            <div className="operational-empty-column">
                                                <i className={`mdi ${meta.icon}`}></i>
                                                <span>Nenhum item</span>
                                                {columnId === TASK_STATUS.NEW && (
                                                    <button type="button" onClick={onNewTaskClick}>
                                                        <i className="mdi mdi-plus"></i>
                                                        Criar
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    );
                })}
            </div>
        </DragDropContext>
    );
};

export default OperationalKanban;
